import { useListExtras } from '@cocoonspace/sdk-js/domains/extras/extras'
import type { Space } from '@cocoonspace/sdk-js/types/space'
import { useTranslation } from 'next-i18next'
import Image from 'next/image'
import { useMemo } from 'react'
import { Price } from '~/components/price'
import { Card } from '~/components/ui/card'
import {
	Carousel,
	CarouselContent,
	CarouselIndicators,
	CarouselItem,
} from '~/components/ui/carousel'
import { ExtraTitle } from '~/domains/extras/components/atoms/extra-title'
import { SpaceSectionContent } from '~/domains/spaces/components/space-details/space-section-content'
import { twTheme } from '~/theme'
import { SpaceSectionTitle } from './space-section-title'

export interface SpaceExtra {
	id: string
	price?: number
}

export const ExtrasCarousel = ({
	extras,
}: {
	extras: SpaceExtra[]
}) => {
	const { t } = useTranslation()

	return (
		<Carousel
			className='flex flex-col gap-4'
			opts={{
				breakpoints: {
					[`(max-width: ${twTheme.theme.screens.sm})`]: {
						slidesToScroll: 1,
						align: 'center',
						skipSnaps: false,
					},
					[`(min-width: ${twTheme.theme.screens.sm})`]: {
						slidesToScroll: 2,
						align: 'start',
					},
					[`(min-width: ${twTheme.theme.screens.md})`]: {
						slidesToScroll: 3,
					},
					[`(min-width: ${twTheme.theme.screens.lg})`]: {
						slidesToScroll: 4,
					},
				},
			}}
		>
			<CarouselContent className='w-full'>
				{extras.map((extra) => (
					<CarouselItem
						key={extra.id}
						className='relative flex pt-[75px] sm:basis-1/2 md:basis-1/3 lg:basis-1/4'
					>
						<Card className='relative p-4 pt-8 text-center'>
							<div className='-translate-x-1/2 -top-[75px] absolute left-1/2 z-10 flex h-[100px] w-[100px] flex-col items-center justify-end'>
								<Image
									src={`/images/v2/extras/${extra.id.toLowerCase()}.svg`}
									alt={extra.id}
									width={100}
									height={100}
								/>
							</div>

							<div className='min-h-[50px]'>
								<ExtraTitle
									extraId={extra.id}
									type='multiline'
								/>
							</div>

							<Price price={extra.price} />

							<p className='mb-4 text-xxs'>{t('extras:per_person')}</p>

							<p className='pb-2 text-xs'>
								{t(`extras:${extra.id}.description`)}
							</p>
						</Card>
					</CarouselItem>
				))}
			</CarouselContent>

			<CarouselIndicators />
		</Carousel>
	)
}

export const SpaceExtras = ({
	space,
}: {
	space: Space
}) => {
	const { t } = useTranslation()

	const { data } = useListExtras(undefined, {
		query: {
			select: (res) => res.data.filter((extra) => extra.category !== 'other'),
		},
	})

	const extras = useMemo(
		() =>
			space?.extras?.map((extra) => ({
				...extra,
				...data?.find((item) => item.id === extra.id),
			})),
		[space?.extras, data],
	)

	return (
		<SpaceSectionContent>
			<SpaceSectionTitle>
				{t('space:sections.extras.title.long')}
			</SpaceSectionTitle>

			<p>{t('space:sections.extras.body')}</p>

			<div className='grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3'>
				{['products', 'choices', 'fast'].map((key) => (
					<div
						key={key}
						className='flex flex-col text-sm'
					>
						<div className='flex flex-row items-center gap-2 font-bold'>
							<div className='text-3xl'>
								{t(`space:sections.extras.explain.${key}.emoji`)}
							</div>

							{t(`space:sections.extras.explain.${key}.title`)}
						</div>

						{t(`space:sections.extras.explain.${key}.subtitle`)}
					</div>
				))}
			</div>

			{extras?.length && <ExtrasCarousel extras={extras} />}
		</SpaceSectionContent>
	)
}
